// Import API modules
import mommApi from './api/mommApi';

// Configuration for API mode
// Check if browser environment and force online mode
const isBrowser = !window.require || !window.sqliteAPI;
let useOnlineApi = isBrowser ? true : (localStorage.getItem('momm_use_online_api') === 'true');

// Debug logging for API mode initialization
console.log('🔧 SQLiteDB initialization:', {
  isBrowser,
  hasWindowRequire: !!window.require,
  hasWindowSqliteAPI: !!window.sqliteAPI,
  useOnlineApi,
  localStorageValue: localStorage.getItem('momm_use_online_api')
});

// Helper to check if API mode is enabled
export const getUseOnlineApi = () => useOnlineApi;

// Helper to set API mode
export const setUseOnlineApi = (value) => {
  // Don't allow changing API mode in browser environment
  if (isBrowser) {
    useOnlineApi = true;
    return;
  }
  useOnlineApi = value;
  localStorage.setItem('momm_use_online_api', value.toString());
};

// Safely require modules with fallbacks
let sqlite3, path, fs;

try {
  sqlite3 = window.require ? window.require('sqlite3').verbose() : null;
  path = window.require ? window.require('path') : null;
  fs = window.require ? window.require('fs') : null;
} catch (e) {
  console.error('Failed to load required modules for SQLite:', e);
  // Set up dummy objects so the code doesn't crash
  sqlite3 = null;
  path = { join: (...args) => args.join('/') };
  fs = { existsSync: () => false };
}

// Use a default path that will be updated once we get the actual user data path
let dbPath = './promptus-sqlite.db';

// Function to initialize the database path
async function initDbPath() {
  try {
    // Try to get the user data path from the exposed API
    if (window.sqliteAPI && window.sqliteAPI.getUserDataPath) {
      const userDataPath = await window.sqliteAPI.getUserDataPath();
      if (userDataPath && path) {
        dbPath = path.join(userDataPath, 'promptus-sqlite.db');
        console.log('SQLite database path initialized to:', dbPath);
      }
    }
  } catch (error) {
    console.warn('Failed to get user data path from IPC, using default path:', error);
  }
}

// Initialize the path
initDbPath().catch(err => {
  console.warn('Error initializing database path:', err);
});

// Initialize SQLite database - only if not using API mode
let db;

if (!getUseOnlineApi()) {
  if (!sqlite3) {
    console.warn('SQLite is not available. Switching to online API mode.');
    setUseOnlineApi(true);
  } else {
    try {
      db = new sqlite3.Database(dbPath);
      console.log('SQLite database initialized at:', dbPath);
    } catch (e) {
      console.error('Failed to create SQLite database:', e);
      console.warn('Switching to online API mode due to SQLite initialization failure.');
      setUseOnlineApi(true);
    }
  }
} else {
  console.log('Using online API mode for MOMM data storage');
}

// Import the database migration module
const { migrateMommDatabase, isMigrationNeeded } = require('./database_migration');

// Initialize the database with enhanced schema and migration support
export const initDatabase = async () => {
  // Skip database initialization if using API mode
  if (getUseOnlineApi()) {
    console.log('✅ Using online API mode - skipping SQLite initialization');
    return true;
  }
  
  // Check if db is available
  if (!db) {
    console.warn('Database not available, switching to API mode');
    setUseOnlineApi(true);
    return true;
  }
  
  try {
    console.log('Initializing MOMM SQLite database...');
    
    // Enable foreign keys for data integrity
    await new Promise((resolve, reject) => {
      db.run('PRAGMA foreign_keys = ON;', (err) => {
        if (err) {
          console.error('Error enabling foreign keys:', err);
          reject(err);
        } else {
          resolve();
        }
      });
    });
    
    // Check if migration is needed and run it
    const needsMigration = await isMigrationNeeded(db);
    if (needsMigration) {
      console.log('Database migration needed, running migration...');
      await migrateMommDatabase(db);
      console.log('Database migration completed successfully');
    } else {
      console.log('Database schema is up to date');
    }
    
    console.log('✅ SQLite database initialized successfully');
    return true;
  } catch (error) {
    console.error('❌ Failed to initialize SQLite database:', error);
    throw error;
  }
};

// Initialize database on module load
initDatabase().catch(err => {
  console.error('Failed to initialize SQLite database:', err);
});

// MOMM Chats operations
export const mommChats = {
  // Save chat history - supports both SQLite and API
  save: (messages) => {
    // Check if we should use the online API
    if (getUseOnlineApi()) {
      return mommApi.chats.save(messages);
    }
    
    // Check if database is available
    if (!db) {
      console.warn('SQLite database is not available for save operation');
      return Promise.reject(new Error('SQLite database is not available. Please enable online mode in settings.'));
    }
    
    // Original SQLite implementation
    return new Promise(async (resolve, reject) => {
      try {
        console.log('Using SQLite database for save operation');
        
        // Get the chat_id from the first message to optimize the query
        const chatId = messages.length > 0 ? messages[0].chat_id : null;
        
        // Only get existing messages for the current chat to improve performance and accuracy
        const query = chatId ? 
          'SELECT message_id, chat_id FROM mommMessages WHERE chat_id = ?' : 
          'SELECT message_id, chat_id FROM mommMessages';
        const params = chatId ? [chatId] : [];
        
        db.all(query, params, async (err, existingMessages) => {
          if (err) {
            console.error('Error checking existing messages:', err);
            reject(err);
            return;
          }
          console.log(`Found ${existingMessages ? existingMessages.length : 0} existing messages in SQLite for chat ${chatId || 'all chats'}`);
          
          // Create a set of existing message IDs for fast lookup
          // Use both message_id and chat_id for more robust duplicate detection
          const existingIds = new Set(existingMessages.map(m => m.message_id));
          const existingChatMessages = new Set(
            existingMessages
              .filter(m => m.chat_id)
              .map(m => `${m.chat_id}-${m.message_id}`)
          );
          
          // Check if we can use transactions (attempt to begin, catch if one is already active)
          let transactionActive = false;
          
          try {
            // Try to start a transaction
            await new Promise((resolve, reject) => {
              db.run('BEGIN TRANSACTION', (err) => {
                if (err) {
                  // Transaction is likely already active
                  console.warn('Could not start transaction, likely one already in progress. Proceeding without transaction.');
                  reject(err);
                } else {
                  transactionActive = true;
                  resolve();
                }
              });
            });
          } catch (txError) {
            // Continue without a transaction
            transactionActive = false;
          }
          
          try {
            // Process each message
            let savedCount = 0;
            
            for (const message of messages) {
              const messageId = String(message.id);
              const chatId = message.chat_id || null;
              
              // Skip temporary UI messages completely
              if (message.isThinking || message.isLoading || 
                  message.text === "Thinking..." || 
                  message.text === "Loading chat history..." ||
                  message.text === "Loading...") {
                console.log(`Skipping temporary message ${messageId}: ${message.text?.substring(0, 30)}`);
                continue;
              }
              
              // Skip messages with invalid senders
              if (message.sender !== 'user' && message.sender !== 'bot') {
                console.log(`Skipping message ${messageId} with invalid sender: ${message.sender}`);
                continue;
              }
              
              // Skip messages with no content
              if (!message.text && (!message.imageUrls || message.imageUrls.length === 0) && 
                  !message.audioUrl && !message.videoUrl) {
                console.log(`Skipping message ${messageId} with no content`);
                continue;
              }
              
              // VALIDATION: Log chat_id for debugging
              if (chatId) {
                console.log(`💾 Saving message ${messageId} with chat_id: ${chatId}`);
              } else {
                console.warn(`⚠️ Message ${messageId} has no chat_id! This may cause orphaned messages.`);
              }
              
              // Skip messages we've already saved
              if (existingIds.has(messageId)) {
                console.log(`Skipping duplicate message ${messageId} - already exists`);
                continue;
              }
              
              // Double-check with chat_id if available
              if (chatId && existingChatMessages.has(`${chatId}-${messageId}`)) {
                console.log(`Skipping duplicate message ${messageId} in chat ${chatId} - already exists`);
                continue;
              }
              
              // Prepare message data
              const timestamp = message.timestamp || Date.now();
              const sender = message.sender;
              const role = message.role;
              const text = message.text || null;
              const hasMedia = !!(message.imageUrls?.length || message.videoUrl || message.audioUrl);
              
              // Enhanced logging for image URLs
              if (message.imageUrls?.length) {
                console.log(`Saving message ${messageId} with ${message.imageUrls.length} images`);
                message.imageUrls.forEach((url, idx) => {
                  console.log(`  Image ${idx+1}: ${typeof url === 'string' ? url.substring(0, 30) + '...' : 'Not a string'}`);
                });
              }
              
              // Clean up image URLs before saving
              let cleanedMediaUrls = null;
              if (message.imageUrls?.length) {
                // Filter out null/undefined URLs and ensure all URLs are strings
                const validUrls = message.imageUrls
                  .filter(url => url != null)
                  .map(url => {
                    if (typeof url !== 'string') {
                      console.warn(`Found non-string URL in message ${messageId}, converting to string`);
                      return String(url);
                    }
                    return url;
                  });
                
                if (validUrls.length > 0) {
                  cleanedMediaUrls = JSON.stringify(validUrls);
                  console.log(`Saved ${validUrls.length} valid image URLs for message ${messageId}`);
                }
              } else if (message.videoUrl) {
                cleanedMediaUrls = JSON.stringify([message.videoUrl]);
              } else if (message.audioUrl) {
                cleanedMediaUrls = JSON.stringify([message.audioUrl]);
              }
              
              const mediaUrls = cleanedMediaUrls;
              const mediaNames = message.imageNames?.length ? JSON.stringify(message.imageNames) : null;
              
              // Additional metadata that doesn't fit in specific columns
              const metadata = JSON.stringify({
                isThinking: !!message.isThinking,
                isLoading: !!message.isLoading,
                preventScroll: !!message.preventScroll,
                isImportedFromComfyUI: !!message.isImportedFromComfyUI,
                isImportedFromPlayground: !!message.isImportedFromPlayground,
                videoUrl: message.videoUrl || null,
                audioUrl: message.audioUrl || null,
                audioUrls: message.audioUrls || null,  // Save audioUrls array for multiple songs
                model_used: message.model_used || null,  // Save model information for display
                isSongMessage: !!message.isSongMessage  // Save song message flag for toolbar logic
              });
              
              // Insert the message
              const stmt = db.prepare(
                'INSERT INTO mommMessages (message_id, sender, role, text, timestamp, has_media, media_urls, media_names, metadata, chat_id) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)'
              );
              
              await new Promise((resolve, reject) => {
                stmt.run(messageId, sender, role, text, timestamp, hasMedia ? 1 : 0, mediaUrls, mediaNames, metadata, message.chat_id || null, function(err) {
                  stmt.finalize();
                  if (err) {
                    // Check if it's a unique constraint violation (duplicate)
                    if (err.message && err.message.includes('UNIQUE constraint failed')) {
                      console.log(`Message ${messageId} already exists (unique constraint), skipping`);
                      resolve(); // Don't reject, just skip this message
                    } else {
                      console.error('Error saving message:', err);
                      reject(err);
                    }
                    return;
                  }
                  savedCount++;
                  resolve();
                });
              });
            }
            
            // Commit the transaction if we started one
            if (transactionActive) {
              await new Promise((resolve, reject) => {
                db.run('COMMIT', (commitErr) => {
                  if (commitErr) {
                    console.error('Error committing transaction:', commitErr);
                    reject(commitErr);
                    return;
                  }
                  resolve();
                });
              });
            }
            
            console.log(`Saved ${savedCount} new messages to database`);
            resolve(savedCount);
          } catch (error) {
            // If there's an error and we have an active transaction, roll it back
            if (transactionActive) {
              db.run('ROLLBACK', () => {
                console.error('Transaction rolled back due to error:', error);
                reject(error);
              });
            } else {
              // Otherwise just report the error
              console.error('Error saving messages (without transaction):', error);
              reject(error);
            }
          }
        });
      } catch (error) {
        console.error('Error in save chat messages:', error);
        reject(error);
      }
    });
  },
  
  // Get most recent chat history from individual messages
  getLatest: () => {
    if (getUseOnlineApi()) {
      return mommApi.chats.getLatest();
    }
    
    // Check if database is available
    if (!db) {
      console.warn('SQLite database is not available for getLatest operation');
      return Promise.reject(new Error('SQLite database is not available. Please enable online mode in settings.'));
    }
    
    return new Promise((resolve, reject) => {
      db.all('SELECT * FROM mommMessages ORDER BY timestamp ASC', (err, rows) => {
        if (err) {
          console.error('Error getting chat messages:', err);
          reject(err);
          return;
        }
        
        if (rows && rows.length > 0) {
          try {
            // Convert database rows back to message objects
            const messages = rows.map(row => {
              const message = {
                id: parseInt(row.message_id),
                sender: row.sender,
                role: row.role,
                timestamp: row.timestamp,
                chat_id: row.chat_id
              };
              
              // Add text if it exists
              if (row.text) {
                message.text = row.text;
              }
              
              // Add media if it exists
              if (row.has_media) {
                if (row.media_urls) {
                  try {
                    const urls = JSON.parse(row.media_urls);
                    if (urls.length > 0) {
                      // Check metadata first to determine if this is a song message
                      let isSongMessage = false;
                      if (row.metadata) {
                        try {
                          const metadata = JSON.parse(row.metadata);
                          isSongMessage = !!metadata.isSongMessage;
                        } catch (e) {
                          // Ignore metadata parsing errors here
                        }
                      }
                      
                      if (isSongMessage) {
                        // For song messages, set audio URLs, not image URLs
                        message.audioUrls = urls;
                        if (urls.length > 0) {
                          message.audioUrl = urls[0]; // Set primary audio URL
                        }
                      } else {
                        // For non-song messages, set image URLs as before
                        message.imageUrls = urls;
                      }
                    }
                  } catch (e) {
                    console.warn('Error parsing media URLs:', e);
                  }
                }
                
                if (row.media_names) {
                  try {
                    message.imageNames = JSON.parse(row.media_names);
                  } catch (e) {
                    console.warn('Error parsing media names:', e);
                  }
                }
              }
              
              // Add metadata
              if (row.metadata) {
                try {
                  const metadata = JSON.parse(row.metadata);
                  Object.assign(message, metadata);
                  
                  // Explicit assignment for critical flags to ensure they're not lost
                  if (metadata.isSongMessage) {
                    message.isSongMessage = true;
                  }
                  
                } catch (e) {
                  console.warn('Error parsing metadata:', e);
                }
              }
              
              return message;
            });
            
            resolve({
              messages: messages
            });
          } catch (parseError) {
            console.error('Error parsing chat messages:', parseError);
            reject(parseError);
          }
        } else {
          resolve({ messages: [] });
        }
      });
    });
  },
  
  // Clear all chat history - supports both SQLite and API
  clear: () => {
    const isOnline = getUseOnlineApi();
    console.log('🔍 mommChats.clear() called - Online mode:', isOnline);
    
    if (isOnline) {
      console.log('✅ Using MOMM API to clear chats');
      return mommApi.chats.clear();
    }
    
    console.log('📂 Using local SQLite to clear chats');
    return new Promise((resolve, reject) => {
      db.run('DELETE FROM mommMessages', (err) => {
        if (err) {
          console.error('Error clearing chat messages:', err);
          reject(err);
          return;
        }
        // Also clear chat sessions
        db.run('DELETE FROM mommChats', (err) => {
          if (err) {
            console.error('Error clearing chat sessions:', err);
            reject(err);
            return;
          }
          console.log('Chat messages and sessions cleared successfully');
          resolve();
        });
      });
    });
  },
  
  // Clean up old chat records (legacy function for compatibility)
  cleanOldChats: () => {
    // Check if database is available
    if (!db) {
      console.warn('SQLite database is not available for cleanOldChats');
      return Promise.resolve(0);
    }
    
    return new Promise((resolve, reject) => {
      try {
        // Check if we have both old-style and new-style records
        db.get('SELECT COUNT(*) as count FROM mommChats', (err, result) => {
          if (err) {
            // Table might not exist in newer installations
            resolve(0);
            return;
          }
          
          if (result.count > 0) {
            // If we have old-style records, delete them all
            db.run('DELETE FROM mommChats', function(err) {
              if (err) {
                console.error('Error deleting old chat records:', err);
                reject(err);
                return;
              }
              
              console.log(`Cleaned up ${this.changes} old-style chat records`);
              resolve(this.changes);
            });
          } else {
            // No old records to clean
            resolve(0);
          }
        });
      } catch (error) {
        console.error('Error in cleanOldChats:', error);
        reject(error);
      }
    });
  },

  // Create a new chat session - supports both SQLite and API
  createNewChat: () => {
    if (getUseOnlineApi()) {
      return mommApi.chats.createNewChat();
    }
    
    // Check if database is available
    if (!db) {
      console.warn('SQLite database is not available. Please enable online mode or use an Electron environment.');
      return Promise.reject(new Error('SQLite database is not available. Please enable online mode in settings.'));
    }
    
    return new Promise((resolve, reject) => {
      const timestamp = Date.now();
      
      db.run('INSERT INTO mommChats (timestamp, messages, title) VALUES (?, ?, ?)', 
        [timestamp, '[]', 'New Chat'], 
        function(err) {
          if (err) {
            console.error('Error creating new chat:', err);
            reject(err);
            return;
          }
          
          const chatId = this.lastID;
          console.log(`Created new chat with ID: ${chatId}`);
          resolve(chatId);
        }
      );
    });
  },
  
  // Get all chat sessions - supports both SQLite and API
  getAllChats: () => {
    if (getUseOnlineApi()) {
      return mommApi.chats.getAllChats();
    }
    
    return new Promise((resolve, reject) => {
      // Get all chats
      db.all('SELECT id, timestamp, title FROM mommChats ORDER BY timestamp DESC', (err, chats) => {
        if (err) {
          console.error('Error getting all chats:', err);
          reject(err);
          return;
        }
        
        if (!chats || chats.length === 0) {
          resolve([]);
          return;
        }
        
        // For each chat, get the most recent message to display as preview
        const promises = chats.map(chat => {
          return new Promise((resolve, reject) => {
            db.all('SELECT * FROM mommMessages WHERE chat_id = ? ORDER BY timestamp ASC', 
              [chat.id], 
              (err, messages) => {
                if (err) {
                  console.error(`Error getting messages for chat ${chat.id}:`, err);
                  reject(err);
                  return;
                }
                
                // Track message IDs to prevent duplicates
                const seenMessageIds = new Set();
                
                // Convert database rows back to message objects and filter out duplicates
                const processedMessages = messages
                  .filter(row => {
                    // Check if we've seen this message ID before
                    const messageId = String(row.message_id);
                    if (seenMessageIds.has(messageId)) {
                      console.log(`getAllChats: Filtering out duplicate message ID ${messageId} from database results`);
                      return false;
                    }
                    
                    // Add to seen set and keep this message
                    seenMessageIds.add(messageId);
                    return true;
                  })
                  .map(row => {
                    const message = {
                      id: parseInt(row.message_id),
                      sender: row.sender,
                      role: row.role,
                      timestamp: row.timestamp,
                      chat_id: row.chat_id
                    };
                  
                  if (row.text) {
                    message.text = row.text;
                  }
                  
                  if (row.has_media) {
                    if (row.media_urls) {
                      try {
                        const urls = JSON.parse(row.media_urls);
                        if (urls.length > 0) {
                          // Check metadata first to determine if this is a song message
                          let isSongMessage = false;
                          if (row.metadata) {
                            try {
                              const metadata = JSON.parse(row.metadata);
                              isSongMessage = !!metadata.isSongMessage;
                            } catch (e) {
                              // Ignore metadata parsing errors here
                            }
                          }
                          
                          if (isSongMessage) {
                            // For song messages, set audio URLs, not image URLs
                            message.audioUrls = urls;
                            if (urls.length > 0) {
                              message.audioUrl = urls[0]; // Set primary audio URL
                            }
                          } else {
                            // For non-song messages, set image URLs as before
                            message.imageUrls = urls;
                          }
                        }
                      } catch (e) {}
                    }
                    
                    if (row.media_names) {
                      try {
                        message.imageNames = JSON.parse(row.media_names);
                      } catch (e) {}
                    }
                  }
                  
                  // Add metadata properties to message
                  if (row.metadata) {
                    try {
                      const metadata = JSON.parse(row.metadata);
                      
                      // Apply metadata to message
                      Object.assign(message, metadata);
                      
                      // Explicit assignment for critical flags to ensure they're not lost
                      if (metadata.isSongMessage) {
                        message.isSongMessage = true;
                      }
                      
                    } catch (e) {
                      console.warn('Error parsing metadata:', e);
                    }
                  }
                  
                  return message;
                });
                
                chat.messages = processedMessages;
                resolve(chat);
              }
            );
          });
        });
        
        Promise.all(promises)
          .then(results => {
            resolve(results);
          })
          .catch(error => {
            console.error('Error processing chat sessions:', error);
            reject(error);
          });
      });
    });
  },
  
  // Get a specific chat by ID - supports both SQLite and API
  getChatById: (chatId, messageLimit = 200) => {
    if (getUseOnlineApi()) {
      // Pass higher message limit to API (was defaulting to 50)
      return mommApi.chats.getChatById(chatId, messageLimit);
    }
    
    return new Promise(async (resolve, reject) => {
      // First get the chat record
      db.get('SELECT id, timestamp, title FROM mommChats WHERE id = ?', 
        [chatId], 
        async (err, chat) => {
          if (err) {
            console.error(`Error getting chat ${chatId}:`, err);
            reject(err);
            return;
          }
          
          if (!chat) {
            resolve(null);
            return;
          }
          
          // Get all valid audio URLs for this chat to use for validation
          let validAudioUrls = new Set();
          try {
            const chatAudioRecords = await mommAudio.getByChatId(chatId);
            chatAudioRecords.forEach(record => {
              if (record.audioData) {
                validAudioUrls.add(record.audioData);
              }
            });
            console.log(`Found ${validAudioUrls.size} valid audio URLs for chat ${chatId}`);
          } catch (audioErr) {
            console.error('Error fetching audio for validation:', audioErr);
          }
          
          // Get all messages for this chat
          db.all('SELECT * FROM mommMessages WHERE chat_id = ? ORDER BY timestamp ASC', 
            [chatId], 
            (err, messages) => {
              if (err) {
                console.error(`Error getting messages for chat ${chatId}:`, err);
                reject(err);
                return;
              }
              
              // Track message IDs to prevent duplicates
              const seenMessageIds = new Set();
              
              // Convert database rows back to message objects and filter out duplicates
              const processedMessages = messages
                .filter(row => {
                  // Check if we've seen this message ID before
                  const messageId = String(row.message_id);
                  if (seenMessageIds.has(messageId)) {
                    console.log(`Filtering out duplicate message ID ${messageId} from database results`);
                    return false;
                  }
                  
                  // Add to seen set and keep this message
                  seenMessageIds.add(messageId);
                  return true;
                })
                .map(row => {
                  console.log(`=== LOADING MESSAGE ${row.message_id} ===`);
                  console.log(`has_media: ${row.has_media}, media_urls: ${row.media_urls ? 'exists' : 'null'}`);
                  
                  const message = {
                    id: parseInt(row.message_id),
                    sender: row.sender,
                    role: row.role,
                    timestamp: row.timestamp,
                    chat_id: row.chat_id
                  };
                  
                  if (row.text) {
                    message.text = row.text;
                  }
                  
                  if (row.has_media) {
                    console.log(`Message ${row.message_id} has media, processing...`);
                    if (row.media_urls) {
                      try {
                        const urls = JSON.parse(row.media_urls);
                        console.log(`Parsed ${urls.length} URLs:`, urls.map(url => url.substring(0, 50) + '...'));
                        if (urls.length > 0) {
                          // Check metadata first to determine if this is a song message
                          let isSongMessage = false;
                          if (row.metadata) {
                            try {
                              const metadata = JSON.parse(row.metadata);
                              isSongMessage = !!metadata.isSongMessage;
                            } catch (e) {
                              // Ignore metadata parsing errors here
                            }
                          }
                          
                          if (isSongMessage) {
                            // For song messages, verify audio URLs belong to current chat
                            // This prevents songs from other chats appearing in the current chat
                            const validatedUrls = [];
                            for (const url of urls) {
                              // Only include URLs that belong to this chat
                              // If we have valid audio URLs from the database, check against them
                              if (validAudioUrls.size > 0) {
                                if (validAudioUrls.has(url)) {
                                  validatedUrls.push(url);
                                } else {
                                  console.warn(`⚠️ Filtering out audio URL that doesn't belong to chat ${chatId}`);
                                }
                              } else {
                                // If we couldn't load valid URLs (e.g., due to error), 
                                // fall back to trusting URLs stored with the message
                                validatedUrls.push(url);
                              }
                            }
                            
                            if (validatedUrls.length > 0) {
                              message.audioUrls = validatedUrls;
                              message.audioUrl = validatedUrls[0]; // Set primary audio URL
                              console.log(`🎵 Set ${validatedUrls.length} validated audioUrls for song message ${row.message_id} in chat ${chatId}`);
                            } else {
                              console.warn(`⚠️ No valid audio URLs found for song message ${row.message_id} in chat ${chatId} - filtered out ${urls.length} invalid URLs`);
                              // Don't set audioUrls or audioUrl if none are valid
                            }
                          } else {
                            // For non-song messages, set image URLs as before
                            message.imageUrls = urls;
                            console.log(`Set imageUrls on message ${row.message_id}`);
                          }
                        }
                      } catch (e) {
                        console.error(`Error parsing media URLs for message ${row.message_id}:`, e);
                      }
                    }
                    
                    if (row.media_names) {
                      try {
                        message.imageNames = JSON.parse(row.media_names);
                      } catch (e) {}
                    }
                  } else {
                    console.log(`Message ${row.message_id} has no media (has_media=${row.has_media})`);
                  }
                  
                  // ENHANCED metadata parsing with debugging for song messages
                  if (row.metadata) {
                    try {
                      const metadata = JSON.parse(row.metadata);
                      
                      // Debug logging for song messages specifically
                      if (metadata.isSongMessage || metadata.audioUrl || metadata.audioUrls) {
                        console.log(`🎵 DB_LOAD: Song message ${row.message_id} with metadata:`, {
                          isSongMessage: metadata.isSongMessage,
                          hasAudioUrl: !!metadata.audioUrl,
                          hasAudioUrls: !!metadata.audioUrls,
                          audioUrlsLength: metadata.audioUrls?.length || 0
                        });
                      }
                      
                      // Debug logging for loading messages with workIds
                      if (metadata.isLoading && (metadata.songWorkId || metadata.videoWorkId)) {
                        console.log(`🔄 DB_LOAD: Loading message ${row.message_id} with workIds:`, {
                          isLoading: metadata.isLoading,
                          songWorkId: metadata.songWorkId,
                          videoWorkId: metadata.videoWorkId,
                          imageDescription: metadata.imageDescription
                        });
                      }
                      
                      // Apply metadata to message, but validate audio URLs first
                      // Create a copy of metadata to avoid modifying the original
                      const validatedMetadata = { ...metadata };
                      
                      // Validate audio URLs from metadata if present
                      if (validatedMetadata.audioUrl || validatedMetadata.audioUrls) {
                        if (validAudioUrls.size > 0) {
                          // Validate single audioUrl
                          if (validatedMetadata.audioUrl && !validAudioUrls.has(validatedMetadata.audioUrl)) {
                            console.warn(`⚠️ Removing invalid audioUrl from metadata for message ${row.message_id} in chat ${chatId}`);
                            delete validatedMetadata.audioUrl;
                          }
                          
                          // Validate audioUrls array
                          if (validatedMetadata.audioUrls && Array.isArray(validatedMetadata.audioUrls)) {
                            const filteredAudioUrls = validatedMetadata.audioUrls.filter(url => validAudioUrls.has(url));
                            if (filteredAudioUrls.length !== validatedMetadata.audioUrls.length) {
                              console.warn(`⚠️ Filtered ${validatedMetadata.audioUrls.length - filteredAudioUrls.length} invalid audio URLs from metadata for message ${row.message_id}`);
                            }
                            validatedMetadata.audioUrls = filteredAudioUrls.length > 0 ? filteredAudioUrls : null;
                          }
                        }
                      }
                      
                      // Apply validated metadata to message
                      Object.assign(message, validatedMetadata);
                      
                      // CRITICAL: Multiple explicit assignments for song message flag preservation
                      if (metadata.isSongMessage) {
                        message.isSongMessage = true;
                        console.log(`✅ DB_LOAD: Song message flag preserved for message ${row.message_id}`);
                      }
                      
                      // ADDITIONAL FALLBACK: Check if this should be a song message based on audio presence
                      if (!message.isSongMessage && (metadata.audioUrl || (metadata.audioUrls && metadata.audioUrls.length > 0))) {
                        // Only treat as song message if there are no image URLs
                        if (!message.imageUrls || message.imageUrls.length === 0) {
                          message.isSongMessage = true;
                          console.log(`🔧 DB_LOAD: Auto-detected song message for ${row.message_id} based on audio content`);
                        }
                      }
                      
                    } catch (e) {
                      console.error(`❌ DB_LOAD: Failed to parse metadata for message ${row.message_id}:`, e);
                      console.error('Raw metadata:', row.metadata);
                    }
                  }
                  
                  // FINAL VALIDATION: Ensure song messages don't have conflicting data
                  if (message.isSongMessage) {
                    console.log(`🎵 DB_LOAD: Final validation for song message ${row.message_id}:`, {
                      isSongMessage: message.isSongMessage,
                      hasAudioUrl: !!message.audioUrl,
                      hasAudioUrls: !!message.audioUrls,
                      hasImageUrls: !!message.imageUrls
                    });
                  }
                  
                  return message;
                });
              
              chat.messages = processedMessages;
              resolve(chat);
            }
          );
        }
      );
    });
  },
  
  // Get chat messages by chat ID - supports both SQLite and API
  getChatMessages: (chatId) => {
    if (getUseOnlineApi()) {
      return mommApi.chats.getChatMessages(chatId);
    }
    
    return new Promise((resolve, reject) => {
      if (!chatId) {
        console.error('No chat ID provided for getChatMessages');
        resolve([]);
        return;
      }
      
      console.log(`Getting messages for chat ${chatId}`);
      
      // Get all messages for this chat
      db.all('SELECT * FROM mommMessages WHERE chat_id = ? ORDER BY timestamp ASC', 
        [chatId], 
        (err, messages) => {
          if (err) {
            console.error(`Error getting messages for chat ${chatId}:`, err);
            reject(err);
            return;
          }
          
          // Track message IDs to prevent duplicates
          const seenMessageIds = new Set();
          
          // Convert database rows back to message objects and filter out duplicates
          const processedMessages = messages
            .filter(row => {
              // Check if we've seen this message ID before
              const messageId = String(row.message_id);
              if (seenMessageIds.has(messageId)) {
                console.log(`Filtering out duplicate message ID ${messageId} from database results`);
                return false;
              }
              
              // Add to seen set and keep this message
              seenMessageIds.add(messageId);
              return true;
            })
            .map(row => {
              const message = {
                id: parseInt(row.message_id),
                sender: row.sender,
                role: row.role,
                timestamp: row.timestamp,
                chat_id: row.chat_id
              };
              
              if (row.text) {
                message.text = row.text;
              }
              
              if (row.has_media) {
                if (row.media_urls) {
                  try {
                    const urls = JSON.parse(row.media_urls);
                    if (urls.length > 0) {
                      // Check metadata first to determine if this is a song message
                      let isSongMessage = false;
                      if (row.metadata) {
                        try {
                          const metadata = JSON.parse(row.metadata);
                          isSongMessage = !!metadata.isSongMessage;
                        } catch (e) {
                          // Ignore metadata parsing errors here
                        }
                      }
                      
                      if (isSongMessage) {
                        // For song messages, set audio URLs, not image URLs
                        message.audioUrls = urls;
                        if (urls.length > 0) {
                          message.audioUrl = urls[0]; // Set primary audio URL
                        }
                      } else {
                        // For non-song messages, set image URLs as before
                        message.imageUrls = urls;
                      }
                    }
                  } catch (e) {
                    console.error(`Error parsing media URLs for message ${row.message_id}:`, e);
                  }
                }
                
                if (row.media_names) {
                  try {
                    message.imageNames = JSON.parse(row.media_names);
                  } catch (e) {}
                }
              }
              
              // Parse and apply metadata
              if (row.metadata) {
                try {
                  const metadata = JSON.parse(row.metadata);
                  
                  // Apply metadata to message
                  Object.assign(message, metadata);
                  
                  // Explicit assignment for critical flags
                  if (metadata.isSongMessage) {
                    message.isSongMessage = true;
                  }
                  
                  // Check if this should be a song message based on audio presence
                  if (!message.isSongMessage && (metadata.audioUrl || (metadata.audioUrls && metadata.audioUrls.length > 0))) {
                    // Only treat as song message if there are no image URLs
                    if (!message.imageUrls || message.imageUrls.length === 0) {
                      message.isSongMessage = true;
                    }
                  }
                  
                } catch (e) {
                  console.error(`Failed to parse metadata for message ${row.message_id}:`, e);
                }
              }
              
              return message;
            });
          
          console.log(`Retrieved ${processedMessages.length} messages for chat ${chatId}`);
          resolve(processedMessages);
        }
      );
    });
  },
  
  // Update chat title - supports both SQLite and API
  updateChatTitle: (chatId, title) => {
    if (getUseOnlineApi()) {
      return mommApi.chats.updateChatTitle(chatId, title);
    }
    
    return new Promise((resolve, reject) => {
      db.run('UPDATE mommChats SET title = ? WHERE id = ?', 
        [title, chatId], 
        function(err) {
          if (err) {
            console.error(`Error updating title for chat ${chatId}:`, err);
            reject(err);
            return;
          }
          
          console.log(`Updated title for chat ${chatId}`);
          resolve(this.changes);
        }
      );
    });
  },
  
  // Delete a specific chat - supports both SQLite and API
  deleteChat: (chatId) => {
    if (getUseOnlineApi()) {
      return mommApi.chats.deleteChat(chatId);
    }
    
    return new Promise((resolve, reject) => {
      db.run('BEGIN TRANSACTION', (beginErr) => {
        if (beginErr) {
          console.error('Error beginning transaction:', beginErr);
          reject(beginErr);
          return;
        }
        
        // Delete all messages for this chat
        db.run('DELETE FROM mommMessages WHERE chat_id = ?', 
          [chatId], 
          (err) => {
            if (err) {
              db.run('ROLLBACK');
              console.error(`Error deleting messages for chat ${chatId}:`, err);
              reject(err);
              return;
            }
            
            // Delete the chat record
            db.run('DELETE FROM mommChats WHERE id = ?', 
              [chatId], 
              function(err) {
                if (err) {
                  db.run('ROLLBACK');
                  console.error(`Error deleting chat ${chatId}:`, err);
                  reject(err);
                  return;
                }
                
                db.run('COMMIT', (commitErr) => {
                  if (commitErr) {
                    console.error('Error committing transaction:', commitErr);
                    reject(commitErr);
                    return;
                  }
                  
                  console.log(`Deleted chat ${chatId}`);
                  resolve(this.changes);
                });
              }
            );
          }
        );
      });
    });
  },
  
  // Delete all messages for a specific chat - supports both SQLite and API
  deleteAllMessagesForChat: (chatId) => {
    if (getUseOnlineApi()) {
      return mommApi.chats.deleteAllMessagesForChat(chatId);
    }
    
    return new Promise((resolve, reject) => {
      if (!chatId) {
        console.error('Cannot delete messages: No chat ID provided');
        reject(new Error('No chat ID provided'));
        return;
      }
      
      console.log(`Deleting all messages for chat ID ${chatId}`);
      
      db.run('DELETE FROM mommMessages WHERE chat_id = ?', [chatId], function(err) {
        if (err) {
          console.error(`Error deleting messages for chat ${chatId}:`, err);
          reject(err);
          return;
        }
        
        const deletedCount = this.changes;
        console.log(`Successfully deleted ${deletedCount} messages for chat ID ${chatId}`);
        resolve(deletedCount);
      });
    });
  },
  
  // Save a single message - supports both SQLite and API
  saveMessage: (message) => {
    if (getUseOnlineApi()) {
      return mommApi.chats.saveMessage(message);
    }
    
    return new Promise((resolve, reject) => {
      if (!message) {
        reject(new Error('No message provided'));
        return;
      }
      
      try {
        // Prepare message data
        const messageId = String(message.id || message.message_id);
        const chatId = message.chat_id || null;
        const timestamp = message.timestamp || Date.now();
        const sender = message.sender;
        const role = message.role;
        const text = message.text || null;
        const hasMedia = !!(message.imageUrls?.length || message.videoUrl || message.audioUrl);
        
        // For now, disable duplicate checking to ensure all messages get saved
        // The bulk save function already handles duplicates properly
        
        // Handle media
        let mediaUrls = null;
        if (message.imageUrls?.length) {
          mediaUrls = JSON.stringify(message.imageUrls);
        } else if (message.videoUrl) {
          mediaUrls = JSON.stringify([message.videoUrl]);
        } else if (message.audioUrl) {
          mediaUrls = JSON.stringify([message.audioUrl]);
        }
        
        const mediaNames = message.imageNames?.length ? JSON.stringify(message.imageNames) : null;
        
        // Additional metadata
        const metadataObj = {
          isThinking: !!message.isThinking,
          isLoading: !!message.isLoading,
          preventScroll: !!message.preventScroll,
          isImportedFromComfyUI: !!message.isImportedFromComfyUI,
          isImportedFromPlayground: !!message.isImportedFromPlayground,
          videoUrl: message.videoUrl || null,
          audioUrl: message.audioUrl || null,
          audioUrls: message.audioUrls || null,
          model_used: message.model_used || null,
          isSongMessage: !!message.isSongMessage,
          // Add workIds for resuming generation
          songWorkId: message.songWorkId || null,
          videoWorkId: message.videoWorkId || null,
          isVideoMessage: !!message.isVideoMessage,
          imageDescription: message.imageDescription || null,
          generationId: message.generationId || null,  // Add generationId for video extend functionality
          prompt: message.prompt || null  // Add prompt for video extension
        };
        
        // Debug logging for song messages during save
        if (message.isSongMessage || message.audioUrl || message.audioUrls) {
          console.log(`🎵 SAVE_MESSAGE: Saving song message ${messageId} with metadata:`, {
            isSongMessage: metadataObj.isSongMessage,
            hasAudioUrl: !!metadataObj.audioUrl,
            hasAudioUrls: !!metadataObj.audioUrls,
            audioUrlsLength: metadataObj.audioUrls?.length || 0,
            chat_id: chatId
          });
        }
        
        const metadata = JSON.stringify(metadataObj);
        
        // Insert the message directly without duplicate checking
        const stmt = db.prepare(
          'INSERT OR IGNORE INTO mommMessages (message_id, sender, role, text, timestamp, has_media, media_urls, media_names, metadata, chat_id) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)'
        );
        
        stmt.run(
          messageId, 
          sender, 
          role, 
          text, 
          timestamp, 
          hasMedia ? 1 : 0, 
          mediaUrls, 
          mediaNames, 
          metadata, 
          chatId, 
          function(err) {
            stmt.finalize();
            if (err) {
              console.error('Error saving message:', err);
              reject(err);
              return;
            }
            if (this.changes > 0) {
              console.log(`✅ Saved message ${messageId} to database for chat ${chatId}`);
              
              // Additional validation for song messages
              if (message.isSongMessage) {
                console.log(`🎵 SAVE_VALIDATION: Song message ${messageId} saved successfully with isSongMessage flag`);
              }
            } else {
              console.log(`🔄 Message ${messageId} already exists, skipped`);
            }
            resolve(this.lastID);
          }
        );
      } catch (error) {
        console.error('Error in saveMessage:', error);
        reject(error);
      }
    });
  },
  
  // Generate AI Response - supports both SQLite and API
  generateAIResponse: async (chatId, text, options = {}) => {
    if (getUseOnlineApi()) {
      return mommApi.chats.generateAIResponse(chatId, text, options);
    }
    
    // In offline mode, we can't generate AI responses
    throw new Error('AI response generation is only available in online mode');
  },
  
  // Search messages across chats
  searchMessages: (query, options = {}) => {
    if (getUseOnlineApi()) {
      return mommApi.chats.searchMessages(query, options);
    }
    
    return new Promise((resolve, reject) => {
      if (!query) {
        resolve([]);
        return;
      }
      
      const limit = options.limit || 50;
      const offset = options.offset || 0;
      const chatId = options.chat_id;
      
      let sql = `
        SELECT * FROM mommMessages 
        WHERE text LIKE ?
      `;
      
      const params = [`%${query}%`];
      
      if (chatId) {
        sql += ' AND chat_id = ?';
        params.push(chatId);
      }
      
      sql += ' ORDER BY timestamp DESC LIMIT ? OFFSET ?';
      params.push(limit, offset);
      
      db.all(sql, params, (err, rows) => {
        if (err) {
          console.error('Error searching messages:', err);
          reject(err);
          return;
        }
        
        const messages = (rows || []).map(row => ({
          id: parseInt(row.id),
          sender: row.sender,
          role: row.role,
          text: row.text,
          timestamp: row.timestamp,
          chat_id: row.chat_id,
          imageUrls: row.media_urls ? JSON.parse(row.media_urls) : [],
          imageNames: row.media_names ? JSON.parse(row.media_names) : []
        }));
        
        resolve(messages);
      });
    });
  },
  
  // Get chat statistics
  getChatStatistics: () => {
    if (getUseOnlineApi()) {
      return mommApi.chats.getChatStatistics();
    }
    
    return new Promise((resolve, reject) => {
      const stats = {
        total_chats: 0,
        total_messages: 0,
        active_chats: 0,
        messages_today: 0,
        messages_this_week: 0,
        messages_this_month: 0
      };
      
      // Get total chats
      db.get('SELECT COUNT(*) as count FROM mommChats', (err, row) => {
        if (err) {
          console.error('Error getting chat count:', err);
          reject(err);
          return;
        }
        
        stats.total_chats = row?.count || 0;
        
        // Get total messages
        db.get('SELECT COUNT(*) as count FROM mommMessages', (err, row) => {
          if (err) {
            console.error('Error getting message count:', err);
            reject(err);
            return;
          }
          
          stats.total_messages = row?.count || 0;
          
          // Get messages today
          const todayStart = new Date().setHours(0, 0, 0, 0);
          db.get(
            'SELECT COUNT(*) as count FROM mommMessages WHERE timestamp >= ?',
            [todayStart],
            (err, row) => {
              if (err) {
                console.error('Error getting today message count:', err);
                reject(err);
                return;
              }
              
              stats.messages_today = row?.count || 0;
              
              // Get messages this week
              const weekStart = new Date();
              weekStart.setDate(weekStart.getDate() - 7);
              db.get(
                'SELECT COUNT(*) as count FROM mommMessages WHERE timestamp >= ?',
                [weekStart.getTime()],
                (err, row) => {
                  if (err) {
                    console.error('Error getting week message count:', err);
                    reject(err);
                    return;
                  }
                  
                  stats.messages_this_week = row?.count || 0;
                  
                  // Get messages this month
                  const monthStart = new Date();
                  monthStart.setDate(1);
                  monthStart.setHours(0, 0, 0, 0);
                  db.get(
                    'SELECT COUNT(*) as count FROM mommMessages WHERE timestamp >= ?',
                    [monthStart.getTime()],
                    (err, row) => {
                      if (err) {
                        console.error('Error getting month message count:', err);
                        reject(err);
                        return;
                      }
                      
                      stats.messages_this_month = row?.count || 0;
                      
                      // Get active chats (chats with messages in last 7 days)
                      db.get(
                        'SELECT COUNT(DISTINCT chat_id) as count FROM mommMessages WHERE timestamp >= ?',
                        [weekStart.getTime()],
                        (err, row) => {
                          if (err) {
                            console.error('Error getting active chat count:', err);
                            reject(err);
                            return;
                          }
                          
                          stats.active_chats = row?.count || 0;
                          resolve(stats);
                        }
                      );
                    }
                  );
                }
              );
            }
          );
        });
      });
    });
  }
};

// MOMM Images operations
export const mommImages = {
  // Save image data with chat and message linking
  save: (hash, imageData, promptText, chatId = null, messageId = null, imageName = null, imageOrder = 0) => {
    if (getUseOnlineApi()) {
      return mommApi.images.save(hash, imageData, promptText, chatId, messageId, imageName, imageOrder);
    }
    
    return new Promise((resolve, reject) => {
      const timestamp = Date.now();
      db.run(
        'INSERT OR REPLACE INTO mommImages (hash, imageData, prompt, timestamp, chat_id, message_id, image_name, image_order) VALUES (?, ?, ?, ?, ?, ?, ?, ?)',
        [hash, imageData, promptText || '', timestamp, chatId, messageId, imageName, imageOrder],
        function (err) {
          if (err) {
            console.error('Error saving image to database:', err);
            reject(err);
            return;
          }
          resolve(this.lastID);
        }
      );
    });
  },
  
  // Get image by hash
  getByHash: (hash) => {
    if (getUseOnlineApi()) {
      return mommApi.images.getByHash(hash);
    }
    
    return new Promise((resolve, reject) => {
      db.get('SELECT * FROM mommImages WHERE hash = ?', [hash], (err, row) => {
        if (err) {
          console.error('Error getting image by hash:', err);
          reject(err);
          return;
        }
        
        if (row) {
          try {
            // Convert binary data to base64 string if needed
            if (row.imageData && !row.imageData.toString().startsWith('http')) {
              // Check if it's a Buffer
              if (Buffer.isBuffer(row.imageData)) {
                row.imageData = row.imageData.toString('base64');
                console.log(`Converted binary to base64, length: ${row.imageData.length}`);
                
                // Test - check first few chars to make sure it's valid base64
                const firstFewChars = row.imageData.substring(0, 20);
                console.log(`First few chars of base64: ${firstFewChars}`);
                
                // Check if it already starts with the data URL prefix
                if (!row.imageData.startsWith('data:')) {
                  // No prefix - this is raw base64, which is what we want
                  console.log("Raw base64 data detected (no prefix)");
                }
              }
            }
          } catch (conversionError) {
            console.error('Error converting image data:', conversionError);
            // Return the original row data if conversion fails
          }
        }
        
        resolve(row);
      });
    });
  },
  
  // Get recent images
  getRecent: (limit = 5) => {
    if (getUseOnlineApi()) {
      return mommApi.images.getRecent(limit);
    }
    
    return new Promise((resolve, reject) => {
      const query = `SELECT * FROM mommImages ORDER BY timestamp DESC LIMIT ?`;
      
      db.all(query, [limit], (err, rows) => {
        if (err) {
          console.error('Error getting recent images:', err);
          reject(err);
          return;
        }
        
        // Process each row to convert binary data if needed
        const processedRows = rows.map(row => {
          try {
            // Convert binary data to base64 string if needed
            if (row.imageData && !row.imageData.toString().startsWith('http')) {
              // Check if it's a Buffer
              if (Buffer.isBuffer(row.imageData)) {
                row.imageData = row.imageData.toString('base64');
                console.log(`Converted binary to base64 for recent image, length: ${row.imageData.length}`);
              }
            }
            return row;
          } catch (conversionError) {
            console.error('Error converting recent image data:', conversionError);
            return row; // Return the original row data if conversion fails
          }
        });
        
        resolve(processedRows);
      });
    });
  },

  // Get all images for a specific chat
  getByChatId: (chatId) => {
    if (getUseOnlineApi()) {
      return mommApi.images.getByChatId(chatId);
    }
    
    return new Promise((resolve, reject) => {
      if (!chatId) {
        console.warn('No chat ID provided for image retrieval');
        resolve([]);
        return;
      }
      
      db.all(
        'SELECT * FROM mommImages WHERE chat_id = ? ORDER BY timestamp ASC, image_order ASC',
        [chatId],
        (err, rows) => {
          if (err) {
            console.error('Error retrieving images by chat ID:', err);
            reject(err);
            return;
          }
          
          console.log(`Retrieved ${rows.length} images for chat ${chatId}`);
          
          // Process the rows to ensure proper image data format
          const processedRows = rows.map(row => {
            let processedImageData = row.imageData;
            
            // Convert blob to base64 string if needed
            if (row.imageData && typeof row.imageData === 'object') {
              try {
                processedImageData = Buffer.from(row.imageData).toString('base64');
                if (!processedImageData.startsWith('data:')) {
                  processedImageData = `data:image/png;base64,${processedImageData}`;
                }
              } catch (conversionError) {
                console.error('Error converting image data:', conversionError);
                processedImageData = row.imageData;
              }
            }
            
            return {
              ...row,
              imageData: processedImageData
            };
          });
          
          resolve(processedRows);
        }
      );
    });
  }
};

// MOMM Audio operations
export const mommAudio = {
  // Save audio data with chat and message linking
  save: (hash, audioData, transcript = null, chatId = null, messageId = null) => {
    if (getUseOnlineApi()) {
      return mommApi.audio.save(hash, audioData, transcript, chatId, messageId);
    }
    
    return new Promise((resolve, reject) => {
      try {
        console.log('🎵 MOMM_AUDIO.SAVE DEBUG - Received parameters:', {
          hash: hash,
          audioDataType: typeof audioData,
          audioDataLength: audioData?.length,
          transcript: transcript,
          chatId: chatId,
          messageId: messageId,
          hasChatId: !!chatId
        });
        
        console.log(`Saving audio with hash: ${hash}, data length: ${audioData.length}, chat_id: ${chatId}, message_id: ${messageId}`);
        
        // Convert base64 string to binary buffer if it's not already binary
        let binaryData;
        if (typeof audioData === 'string') {
          // If it's a data URL, extract the base64 part
          if (audioData.includes('data:audio') && audioData.includes('base64,')) {
            const base64Data = audioData.split('base64,')[1];
            binaryData = Buffer.from(base64Data, 'base64');
            console.log(`Converted audio data URL to binary, size: ${binaryData.length} bytes`);
          } else if (audioData.startsWith('http')) {
            // For URLs, just store as is (as string)
            binaryData = audioData;
            console.log(`Storing audio URL as is: ${audioData.substring(0, 50)}...`);
          } else {
            // Assume it's already base64
            binaryData = Buffer.from(audioData, 'base64');
            console.log(`Converted audio base64 to binary, size: ${binaryData.length} bytes`);
          }
        } else {
          // If it's already a buffer, use as is
          binaryData = audioData;
          console.log(`Using provided audio binary data, size: ${binaryData.length} bytes`);
        }
        
        const stmt = db.prepare(
          'INSERT OR REPLACE INTO mommAudio (hash, audioData, transcript, timestamp, chat_id, message_id) VALUES (?, ?, ?, ?, ?, ?)'
        );
        const timestamp = Date.now();
        
        console.log('🎵 About to execute SQL INSERT with values:', {
          hash: hash,
          binaryDataType: typeof binaryData,
          transcript: transcript,
          timestamp: timestamp,
          chatId: chatId,
          messageId: messageId
        });
        
        stmt.run(hash, binaryData, transcript, timestamp, chatId, messageId, function(err) {
          stmt.finalize();
          if (err) {
            console.error('Error saving audio data:', err);
            reject(err);
            return;
          }
          console.log(`Successfully saved audio with hash: ${hash}, ID: ${this.lastID}, chat_id: ${chatId}`);
          resolve(this.lastID);
        });
      } catch (error) {
        console.error('Error in audio save function:', error);
        reject(error);
      }
    });
  },
  
  // Get audio by hash
  getByHash: (hash) => {
    if (getUseOnlineApi()) {
      return mommApi.audio.getByHash(hash);
    }
    
    return new Promise((resolve, reject) => {
      console.log(`Looking for audio with hash: ${hash}`);
      db.get('SELECT * FROM mommAudio WHERE hash = ?', [hash], (err, row) => {
        if (err) {
          console.error('Error getting audio by hash:', err);
          reject(err);
          return;
        }
        
        if (row) {
          console.log(`Found audio with hash: ${hash}`);
          try {
            // Convert binary data to base64 string if needed
            if (row.audioData && !row.audioData.toString().startsWith('http')) {
              // Check if it's a Buffer
              if (Buffer.isBuffer(row.audioData)) {
                row.audioData = row.audioData.toString('base64');
                console.log(`Converted audio binary to base64, length: ${row.audioData.length}`);
              } else if (typeof row.audioData === 'object') {
                // Handle potential ArrayBuffer or Uint8Array
                row.audioData = Buffer.from(row.audioData).toString('base64');
                console.log(`Converted audio binary object to base64, length: ${row.audioData.length}`);
              }
              // If it's a string and not a URL, assume it's already base64
            }
          } catch (conversionError) {
            console.error('Error converting audio data from binary:', conversionError);
            // Return the data as is if conversion fails
          }
        } else {
          console.log(`No audio found with hash: ${hash}`);
        }
        
        resolve(row);
      });
    });
  },
  
  // Get all audio for a specific chat
  getByChatId: (chatId) => {
    if (getUseOnlineApi()) {
      return mommApi.audio.getByChatId(chatId);
    }
    
    return new Promise((resolve, reject) => {
      if (!chatId) {
        console.warn('No chat ID provided for audio retrieval');
        resolve([]);
        return;
      }
      
      db.all(
        'SELECT * FROM mommAudio WHERE chat_id = ? ORDER BY timestamp ASC',
        [chatId],
        (err, rows) => {
          if (err) {
            console.error('Error retrieving audio by chat ID:', err);
            reject(err);
            return;
          }
          
          console.log(`Retrieved ${rows.length} audio files for chat ${chatId}`);
          resolve(rows);
        }
      );
    });
  }
};

// MOMM Videos operations
export const mommVideos = {
  // Save video data with chat and message linking
  save: (hash, videoData, prompt = null, chatId = null, messageId = null, generationId = null) => {
    if (getUseOnlineApi()) {
      return mommApi.videos.save(hash, videoData, prompt, chatId, messageId, generationId);
    }
    
    return new Promise((resolve, reject) => {
      try {
        console.log('🎬 MOMM_VIDEOS.SAVE DEBUG - Received parameters:', {
          hash: hash,
          videoDataType: typeof videoData,
          videoDataLength: videoData?.length,
          prompt: prompt,
          chatId: chatId,
          messageId: messageId,
          generationId: generationId,
          hasChatId: !!chatId
        });
        
        console.log(`Saving video with hash: ${hash}, data length: ${videoData.length}, chat_id: ${chatId}, message_id: ${messageId}`);
        
        // Convert base64 string to binary buffer if it's not already binary
        let binaryData;
        if (typeof videoData === 'string') {
          // If it's a data URL, extract the base64 part
          if (videoData.includes('data:video') && videoData.includes('base64,')) {
            const base64Data = videoData.split('base64,')[1];
            binaryData = Buffer.from(base64Data, 'base64');
            console.log(`Converted video data URL to binary, size: ${binaryData.length} bytes`);
          } else if (videoData.startsWith('http')) {
            // For URLs, just store as is (as string)
            binaryData = videoData;
            console.log(`Storing video URL as is: ${videoData.substring(0, 50)}...`);
          } else {
            // Assume it's already base64
            binaryData = Buffer.from(videoData, 'base64');
            console.log(`Converted video base64 to binary, size: ${binaryData.length} bytes`);
          }
        } else {
          // If it's already a buffer, use as is
          binaryData = videoData;
          console.log(`Using provided video binary data, size: ${binaryData.length} bytes`);
        }
        
        const stmt = db.prepare(
          'INSERT OR REPLACE INTO mommVideos (hash, videoData, prompt, timestamp, chat_id, message_id, generation_id) VALUES (?, ?, ?, ?, ?, ?, ?)'
        );
        const timestamp = Date.now();
        
        console.log('🎬 About to execute SQL INSERT with values:', {
          hash: hash,
          binaryDataType: typeof binaryData,
          prompt: prompt,
          timestamp: timestamp,
          chatId: chatId,
          messageId: messageId,
          generationId: generationId
        });
        
        stmt.run(hash, binaryData, prompt, timestamp, chatId, messageId, generationId, function(err) {
          stmt.finalize();
          if (err) {
            console.error('Error saving video data:', err);
            reject(err);
            return;
          }
          console.log(`Successfully saved video with hash: ${hash}, ID: ${this.lastID}, chat_id: ${chatId}`);
          resolve(this.lastID);
        });
      } catch (error) {
        console.error('Error in video save function:', error);
        reject(error);
      }
    });
  },
  
  // Get video by hash
  getByHash: (hash) => {
    if (getUseOnlineApi()) {
      return mommApi.videos.getByHash(hash);
    }
    
    return new Promise((resolve, reject) => {
      console.log(`Looking for video with hash: ${hash}`);
      db.get('SELECT * FROM mommVideos WHERE hash = ?', [hash], (err, row) => {
        if (err) {
          console.error('Error getting video by hash:', err);
          reject(err);
          return;
        }
        
        if (row) {
          console.log(`Found video with hash: ${hash}`);
          try {
            // Convert binary data to base64 string if needed
            if (row.videoData && !row.videoData.toString().startsWith('http')) {
              // Check if it's a Buffer
              if (Buffer.isBuffer(row.videoData)) {
                row.videoData = row.videoData.toString('base64');
                console.log(`Converted video binary to base64, length: ${row.videoData.length}`);
              } else if (typeof row.videoData === 'object') {
                // Handle potential ArrayBuffer or Uint8Array
                row.videoData = Buffer.from(row.videoData).toString('base64');
                console.log(`Converted video binary object to base64, length: ${row.videoData.length}`);
              }
              // If it's a string and not a URL, assume it's already base64
            }
          } catch (conversionError) {
            console.error('Error converting video data from binary:', conversionError);
            // Return the data as is if conversion fails
          }
        } else {
          console.log(`No video found with hash: ${hash}`);
        }
        
        resolve(row);
      });
    });
  }
};

// MOMM Collections operations
export const mommCollections = {
  // Create a new collection
  create: (name, description = '') => {
    if (getUseOnlineApi()) {
      return mommApi.collections.create(name, description);
    }
    
    return new Promise((resolve, reject) => {
      const timestamp = Date.now();
      db.run(
        'INSERT INTO mommCollections (name, description, created_at, updated_at) VALUES (?, ?, ?, ?)',
        [name, description, timestamp, timestamp],
        function (err) {
          if (err) {
            console.error('Error creating collection:', err);
            reject(err);
            return;
          }
          console.log(`Created collection "${name}" with ID: ${this.lastID}`);
          resolve(this.lastID);
        }
      );
    });
  },

  // Get all collections
  getAll: () => {
    if (getUseOnlineApi()) {
      return mommApi.collections.getAll();
    }
    
    return new Promise((resolve, reject) => {
      db.all('SELECT * FROM mommCollections ORDER BY created_at DESC', (err, rows) => {
        if (err) {
          console.error('Error getting collections:', err);
          reject(err);
          return;
        }
        resolve(rows || []);
      });
    });
  },

  // Get collection by ID
  getById: (collectionId) => {
    if (getUseOnlineApi()) {
      return mommApi.collections.getById(collectionId);
    }
    
    return new Promise((resolve, reject) => {
      db.get('SELECT * FROM mommCollections WHERE id = ?', [collectionId], (err, row) => {
        if (err) {
          console.error('Error getting collection by ID:', err);
          reject(err);
          return;
        }
        resolve(row);
      });
    });
  },

  // Update collection
  update: (collectionId, name, description) => {
    if (getUseOnlineApi()) {
      return mommApi.collections.update(collectionId, name, description);
    }
    
    return new Promise((resolve, reject) => {
      db.run(
        'UPDATE mommCollections SET name = ?, description = ?, updated_at = ? WHERE id = ?',
        [name, description, Date.now(), collectionId],
        function (err) {
          if (err) {
            console.error('Error updating collection:', err);
            reject(err);
            return;
          }
          resolve(this.changes > 0);
        }
      );
    });
  },

  // Delete collection
  delete: (collectionId) => {
    if (getUseOnlineApi()) {
      return mommApi.collections.delete(collectionId);
    }
    
    return new Promise((resolve, reject) => {
      db.run('DELETE FROM mommCollections WHERE id = ?', [collectionId], function (err) {
        if (err) {
          console.error('Error deleting collection:', err);
          reject(err);
          return;
        }
        resolve(this.changes > 0);
      });
    });
  },

  // Add image to collection
  addImage: (imageId, collectionId, position = 0) => {
    if (getUseOnlineApi()) {
      return mommApi.collections.addImage(imageId, collectionId, position);
    }
    
    return new Promise((resolve, reject) => {
      db.run(
        'INSERT OR IGNORE INTO mommImageCollections (image_id, collection_id, position) VALUES (?, ?, ?)',
        [imageId, collectionId, position],
        function (err) {
          if (err) {
            console.error('Error adding image to collection:', err);
            reject(err);
            return;
          }
          console.log(`Added image ${imageId} to collection ${collectionId}`);
          resolve(this.lastID);
        }
      );
    });
  },

  // Remove image from collection
  removeImage: (imageId, collectionId) => {
    if (getUseOnlineApi()) {
      return mommApi.collections.removeImage(imageId, collectionId);
    }
    
    return new Promise((resolve, reject) => {
      db.run(
        'DELETE FROM mommImageCollections WHERE image_id = ? AND collection_id = ?',
        [imageId, collectionId],
        function (err) {
          if (err) {
            console.error('Error removing image from collection:', err);
            reject(err);
            return;
          }
          console.log(`Removed image ${imageId} from collection ${collectionId}`);
          resolve(this.changes > 0);
        }
      );
    });
  },

  // Get all images in a collection
  getImages: (collectionId) => {
    if (getUseOnlineApi()) {
      return mommApi.collections.getImages(collectionId);
    }
    
    return new Promise((resolve, reject) => {
      const query = `
        SELECT i.*, ic.added_at, ic.position 
        FROM mommImages i 
        JOIN mommImageCollections ic ON i.id = ic.image_id 
        WHERE ic.collection_id = ? 
        ORDER BY ic.position ASC, ic.added_at DESC
      `;
      
      db.all(query, [collectionId], (err, rows) => {
        if (err) {
          console.error('Error getting collection images:', err);
          reject(err);
          return;
        }
        resolve(rows || []);
      });
    });
  },

  // Get collections containing a specific image
  getByImageId: (imageId) => {
    if (getUseOnlineApi()) {
      return mommApi.collections.getByImageId(imageId);
    }
    
    return new Promise((resolve, reject) => {
      const query = `
        SELECT c.*, ic.added_at, ic.position 
        FROM mommCollections c 
        JOIN mommImageCollections ic ON c.id = ic.collection_id 
        WHERE ic.image_id = ? 
        ORDER BY c.name ASC
      `;
      
      db.all(query, [imageId], (err, rows) => {
        if (err) {
          console.error('Error getting collections for image:', err);
          reject(err);
          return;
        }
        resolve(rows || []);
      });
    });
  }
};

// Enhanced mommImages operations with collection support
export const mommImagesEnhanced = {
  ...mommImages,

  // Mark image as collected
  markAsCollected: (hash, collectionName = null) => {
    if (getUseOnlineApi()) {
      return mommApi.imagesEnhanced.markAsCollected(hash, collectionName);
    }
    
    return new Promise((resolve, reject) => {
      db.run(
        'UPDATE mommImages SET is_collected = TRUE, collection_name = ? WHERE hash = ?',
        [collectionName, hash],
        function (err) {
          if (err) {
            console.error('Error marking image as collected:', err);
            reject(err);
            return;
          }
          resolve(this.changes > 0);
        }
      );
    });
  },

  // Unmark image as collected
  unmarkAsCollected: (hash) => {
    if (getUseOnlineApi()) {
      return mommApi.imagesEnhanced.unmarkAsCollected(hash);
    }
    
    return new Promise((resolve, reject) => {
      db.run(
        'UPDATE mommImages SET is_collected = FALSE, collection_name = NULL WHERE hash = ?',
        [hash],
        function (err) {
          if (err) {
            console.error('Error unmarking image as collected:', err);
            reject(err);
            return;
          }
          resolve(this.changes > 0);
        }
      );
    });
  },

  // Get all collected images
  getCollected: () => {
    if (getUseOnlineApi()) {
      return mommApi.imagesEnhanced.getCollected();
    }
    
    return new Promise((resolve, reject) => {
      db.all('SELECT * FROM mommImages WHERE is_collected = TRUE ORDER BY timestamp DESC', (err, rows) => {
        if (err) {
          console.error('Error getting collected images:', err);
          reject(err);
          return;
        }
        resolve(rows || []);
      });
    });
  },

  // Search images by prompt or tags
  search: (query) => {
    if (getUseOnlineApi()) {
      return mommApi.imagesEnhanced.search(query);
    }
    
    return new Promise((resolve, reject) => {
      const searchQuery = `
        SELECT * FROM mommImages 
        WHERE prompt LIKE ? OR tags LIKE ? 
        ORDER BY timestamp DESC
      `;
      const searchTerm = `%${query}%`;
      
      db.all(searchQuery, [searchTerm, searchTerm], (err, rows) => {
        if (err) {
          console.error('Error searching images:', err);
          reject(err);
          return;
        }
        resolve(rows || []);
      });
    });
  },

  // Add tags to image
  addTags: (hash, tags) => {
    if (getUseOnlineApi()) {
      return mommApi.imagesEnhanced.addTags(hash, tags);
    }
    
    return new Promise((resolve, reject) => {
      // First get current tags
      db.get('SELECT tags FROM mommImages WHERE hash = ?', [hash], (err, row) => {
        if (err) {
          reject(err);
          return;
        }
        
        if (!row) {
          reject(new Error('Image not found'));
          return;
        }
        
        let currentTags = [];
        try {
          currentTags = row.tags ? JSON.parse(row.tags) : [];
        } catch (e) {
          currentTags = [];
        }
        
        // Add new tags
        const newTags = Array.isArray(tags) ? tags : [tags];
        const updatedTags = [...new Set([...currentTags, ...newTags])];
        
        // Update database
        db.run(
          'UPDATE mommImages SET tags = ? WHERE hash = ?',
          [JSON.stringify(updatedTags), hash],
          function (err) {
            if (err) {
              console.error('Error adding tags to image:', err);
              reject(err);
              return;
            }
            resolve(this.changes > 0);
          }
        );
      });
    });
  }
};

// Ensure a table exists (migration helper)
export const ensureTableExists = async (tableName, schema) => {
  // In SQLite, we already handle this with "CREATE TABLE IF NOT EXISTS"
  // This function is included for API compatibility with the original code
  return true;
};

// Close the database connection when the app is shutting down
export const closeDatabase = () => {
  // Skip if using API mode or no database
  if (getUseOnlineApi() || !db) {
    return Promise.resolve();
  }
  
  return new Promise((resolve, reject) => {
    db.close((err) => {
      if (err) {
        console.error('Error closing database:', err);
        reject(err);
        return;
      }
      console.log('SQLite database closed successfully');
      resolve();
    });
  });
};

// // Export the database modules
// module.exports ={
//   initDatabase,
//   closeDatabase,
//   ensureTableExists,
//   mommChats,
//   mommImages,
//   mommImagesEnhanced,
//   mommCollections,
//   mommAudio,
//   mommVideos
// }; 